import { animated, useSpring } from '@react-spring/web';
import classNames from 'classnames';
import React from 'react';
import { Nullable } from 'src/types/nullable.type';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import Minimax from 'tic-tac-toe-minimax';
import { checkWinner } from './check-winner';
import { BoardCell, Player, PlayerWins } from './types';
const { ComputerMove } = Minimax;

const PLAYER_ICON_SVGS: Record<Player, React.ReactNode> = {
  O: (
    <svg className='w-6 h-6 transform-gpu' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='12' fill='#9F4DB6' />
      <path
        d='M6.19922 12C6.19922 8.79676 8.79597 6.20001 11.9992 6.20001C15.2025 6.20001 17.7992 8.79676 17.7992 12C17.7992 15.2033 15.2025 17.8 11.9992 17.8C8.79597 17.8 6.19922 15.2033 6.19922 12Z'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  ),
  X: (
    <svg className='w-6 h-6 transform-gpu' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <rect width='24' height='24' rx='12' fill='#7859BC' />
      <path
        d='M16.2008 7.79999L7.80078 16.2'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M7.80078 7.79999L16.2008 16.2'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  ),
};

const PLAYER_CELL_SVGS: Record<Player, React.ReactNode> = {
  O: (
    <svg className='w-9 h-9 transform-gpu' viewBox='0 0 36 36' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M2 18C2 9.16345 9.16345 2 18 2C26.8366 2 34 9.16345 34 18C34 26.8366 26.8366 34 18 34C9.16345 34 2 26.8366 2 18Z'
        stroke='white'
        strokeWidth='3.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  ),
  X: (
    <svg className='w-[33px] h-[33px] transform-gpu' viewBox='0 0 33 33' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M31.5188 2.98744C32.2022 2.30402 32.2022 1.19598 31.5188 0.512565C30.8354 -0.170853 29.7274 -0.170855 29.044 0.512561L16.0157 13.5408L2.98743 0.512561C2.30402 -0.170855 1.19598 -0.170853 0.512561 0.512565C-0.170855 1.19598 -0.170853 2.30402 0.512565 2.98744L13.5408 16.0156L0.512565 29.0439C-0.170853 29.7273 -0.170855 30.8353 0.512561 31.5187C1.19598 32.2021 2.30402 32.2021 2.98743 31.5187L16.0157 18.4905L29.044 31.5187C29.7274 32.2021 30.8354 32.2021 31.5188 31.5187C32.2022 30.8353 32.2022 29.7273 31.5188 29.0439L18.4906 16.0156L31.5188 2.98744Z'
        fill='white'
      />
    </svg>
  ),
};

const PlayerView = ({ player, winCount }: { player: Player; winCount: number }) => {
  return (
    <div className='flex items-center justify-center'>
      <div className='text-neue-journey-fg'>{PLAYER_ICON_SVGS[player]}</div>
      <div className='relative -left-[2px] w-6 h-6 bg-neue-journey-fg-10 rounded-full flex items-center justify-center text-neue-journey-fg text-neue-journey-small'>
        {winCount}
      </div>
    </div>
  );
};

const BoardCellView = ({ player, onClick }: { player: Nullable<Player>; onClick: () => void }) => {
  return (
    <div
      className={classNames('w-full h-full flex items-center justify-center', 'cursor-pointer', {
        'opacity-0': !player,
        'opacity-100': player,
        'transition-opacity delay-500': player === 'X',
      })}
      onClick={onClick}
    >
      {player ? PLAYER_CELL_SVGS[player] : null}
    </div>
  );
};

function createInitialBoard(): BoardCell[] {
  return Array.from({ length: 9 }, () => null);
  // return [null, 'O', 'X', null, 'O', 'X', null, 'O', null];
  // return ['O', null, 'X', null, 'O', 'X', null, null, 'O'];
}

const PLAYERS: Player[] = ['O', 'X'];

function boardToGrid(boardCells: BoardCell[]) {
  const grid: BoardCell[][] = [];
  for (let i = 0; i < 3; i++) {
    grid.push(boardCells.slice(i * 3, i * 3 + 3));
  }
  return grid;
}

function toRowCol(i: number): [number, number] {
  return [i % 3, Math.floor(i / 3)];
}

function toLineCoordinates(winningIndices: number[]): [number, number][] {
  const BOARD_WIDTH = 200;
  const BOARD_HEIGHT = 200;
  const GAP = 4;

  const [row1, col1] = toRowCol(winningIndices[0]);
  const [row2, col2] = toRowCol(winningIndices[1]);
  const [row3, col3] = toRowCol(winningIndices[2]);

  const CELL_WIDTH = (BOARD_WIDTH - 2 * GAP) / 3;
  const CELL_HEIGHT = (BOARD_HEIGHT - 2 * GAP) / 3;

  const toX = (row: number) => row * (CELL_WIDTH + GAP) + CELL_WIDTH / 2 + (row - 1) * 25;
  const toY = (col: number) => col * (CELL_HEIGHT + GAP) + CELL_HEIGHT / 2 + (col - 1) * 25;

  return [
    [toX(row1), toY(col1)],
    [toX(row2), toY(col2)],
    [toX(row3), toY(col3)],
  ];
}

const WinningLineView = ({
  winningIndices,
  onAnimationComplete,
}: {
  winningIndices: number[];
  onAnimationComplete: () => void;
}) => {
  const coords = toLineCoordinates(winningIndices);

  const [x1, y1] = coords[0];
  const [x2, y2] = coords[1];
  const [x3, y3] = coords[2];

  const length = Math.sqrt((x1 - x3) ** 2 + (y1 - y3) ** 2);

  const props = useSpring({
    from: { strokeDashoffset: length },
    to: { strokeDashoffset: 0 },
    onRest: () => {
      onAnimationComplete();
    },
  });

  return (
    <svg
      className='transform-gpu w-full h-full absolute top-0 left-0 text-neue-journey-accent'
      viewBox='0 0 200 200'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <animated.path
        d={`M ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3}`}
        stroke='currentColor'
        strokeWidth='4'
        strokeLinecap='round'
        strokeDasharray={length}
        strokeDashoffset={props.strokeDashoffset}
      />
    </svg>
  );
};

export const Board = () => {
  const [wins, setWins] = React.useState<PlayerWins>({ O: 0, X: 0 });
  const [board, setBoard] = React.useState<BoardCell[]>(createInitialBoard());
  const [currentPlayer, setCurrentPlayer] = React.useState<Player>('O');
  const [winningIndices, setWinningIndices] = React.useState<Nullable<number[]>>(null);

  const nextMove = useRefCallback(() => {
    const huPlayer = 'O';
    const aiPlayer = 'X';
    const symbols = {
      huPlayer: huPlayer,
      aiPlayer: aiPlayer,
    };
    const difficulty = 'Normal';
    const boardData = board.map((cell, i) => (cell === null ? i : cell));
    // [0, 1, aiPlayer, 3, huPlayer, huPlayer, 6, 7, 8];

    return ComputerMove(boardData, symbols, difficulty);
  }, [board]);

  const markCell = useRefCallback(
    (row: number, col: number) => {
      const index = row * 3 + col;
      if (board[index]) {
        return;
      }
      const newBoard = [...board];
      newBoard[index] = currentPlayer;
      setBoard(newBoard);
      setCurrentPlayer(currentPlayer === 'O' ? 'X' : 'O');
    },
    [board, currentPlayer]
  );

  // Smart logic for computer player X to make the next tic tac toe move
  React.useEffect(() => {
    if (currentPlayer === 'X') {
      const index = nextMove();
      if (index !== undefined) {
        const row = Math.floor(index / 3);
        const col = index % 3;
        markCell(row, col);
      }
    }
  }, [currentPlayer, nextMove]);

  React.useEffect(() => {
    const winningIndices = checkWinner(board);
    if (winningIndices) {
      setWinningIndices(winningIndices);
    } else if (board.every((cell) => cell !== null)) {
      setBoard(createInitialBoard());
      setCurrentPlayer('O');
    }
  }, [board]);

  const onWinningAnimationComplete = useRefCallback(() => {
    if (!winningIndices) {
      return;
    }
    const winner = board[winningIndices[0]];
    if (winner === null) {
      return;
    }
    setWins((wins) => ({ ...wins, [winner]: wins[winner] + 1 }));
    setBoard(createInitialBoard());
    setCurrentPlayer('O');
    setWinningIndices(null);
  }, [board, winningIndices]);

  const boardGrid = boardToGrid(board);

  return (
    <div className='flex flex-col gap-4 items-center justify-center py-8'>
      <div className='flex gap-4'>
        {PLAYERS.map((player) => (
          <PlayerView key={player} player={player} winCount={wins[player]} />
        ))}
      </div>
      <div className='flex flex-col gap-4 items-center justify-center p-2.5'>
        <div className='relative w-[200px] h-[200px]'>
          <svg
            className='transform-gpu w-full h-full absolute top-0 left-0'
            viewBox='0 0 200 200'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M66 0C64.8954 0 64 0.895429 64 2V63C64 64.1046 63.1046 65 62 65H2C0.895431 65 0 65.8954 0 67C0 68.1046 0.895429 69 2 69H62C63.1046 69 64 69.8954 64 71V133C64 134.105 63.1046 135 62 135H2C0.895431 135 0 135.895 0 137C0 138.105 0.895429 139 2 139H62C63.1046 139 64 139.895 64 141V198C64 199.105 64.8954 200 66 200C67.1046 200 68 199.105 68 198V141C68 139.895 68.8954 139 70 139H132C133.105 139 134 139.895 134 141V198C134 199.105 134.895 200 136 200C137.105 200 138 199.105 138 198V141C138 139.895 138.895 139 140 139H198C199.105 139 200 138.105 200 137C200 135.895 199.105 135 198 135H140C138.895 135 138 134.105 138 133V71C138 69.8954 138.895 69 140 69H198C199.105 69 200 68.1046 200 67C200 65.8954 199.105 65 198 65H140C138.895 65 138 64.1046 138 63V2C138 0.895431 137.105 0 136 0C134.895 0 134 0.895429 134 2V63C134 64.1046 133.105 65 132 65H70C68.8954 65 68 64.1046 68 63V2C68 0.895431 67.1046 0 66 0ZM132 135C133.105 135 134 134.105 134 133V71C134 69.8954 133.105 69 132 69H70C68.8954 69 68 69.8954 68 71V133C68 134.105 68.8954 135 70 135H132Z'
              fill='white'
              fillOpacity='0.06'
            />
          </svg>
          <div className='grid grid-cols-3 grid-rows-3 gap-1 w-full h-full'>
            {boardGrid.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <BoardCellView key={colIndex} player={cell} onClick={() => markCell(rowIndex, colIndex)} />
              ))
            )}
          </div>
          {winningIndices && (
            <WinningLineView
              winningIndices={winningIndices}
              onAnimationComplete={() => {
                onWinningAnimationComplete();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
